import type { DtoDeliveryService, DtoPickupPoint, DtoPrice, DtoSaving } from '@repo/api-types/order-retrieval';

import type { TimeWindow } from './time-windows.types';

import type { SubDelivery as DraftSubDelivery, Price11 } from './draft.types';
import type { SubDelivery as QuoteSubDelivery } from './quotation.types';

export const pickupServiceTypes = {
    PUP: 'PUP',
    PUOP: 'PUOP',
    LOCKER: 'LOCKER',
    CLICK_COLLECT_STORE: 'CLICK_COLLECT_STORE',
} as const;

export const serviceTypes = {
    ...pickupServiceTypes,
    HOME_DELIVERY: 'HOME_DELIVERY',
} as const;

export type PickupServiceType = (typeof pickupServiceTypes)[keyof typeof pickupServiceTypes];

export type ServiceType = (typeof serviceTypes)[keyof typeof serviceTypes];

export const isPickupServiceType = (serviceType: unknown): serviceType is PickupServiceType => {
    return Object.values(pickupServiceTypes).some((type) => type === serviceType);
};

export const isServiceType = (serviceType: unknown): serviceType is ServiceType => {
    return Object.values(serviceTypes).some((type) => type === serviceType);
};

export interface Service {
    serviceId?: string;
    // Stable UI identity for a single service instance. Unlike serviceId, sibling services
    // (e.g. two subDeliveries sharing the same serviceItemId) get distinct values, so it is
    // safe to use as a React key / collapse key.
    serviceInstanceId?: string;
    deliveryNo?: string;
    isDelivery?: boolean;
    serviceType?: string;
    serviceName?: string;
    serviceProviderName?: string;
    servicePaymentTo?: string;
    price?: DtoPrice | Price11;
    subDeliveries?: DraftSubDelivery[] | QuoteSubDelivery[];
    timeWindow?: TimeWindow;
    referenceItems?: Array<{
        itemId?: string;
        itemType?: string;
        quantityType?: string;
        name?: string;
        description?: string;
        quantity?: number | undefined;
        image?: string;
        retailItemName?: string;
        retailItemDescription?: string;
        isReferenceItem?: boolean;
        seqNo?: number;
    }>;
    savings?: DtoSaving[];
    status?: string;
    statusText?: string;
    statusCode?: string;
    deliveryDestinationType?: DtoDeliveryService['deliveryDestinationType'];
    pickupPoint?: DtoPickupPoint;
    capabilities?: DtoDeliveryService['capabilities'];
}
